import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation, Trans } from 'react-i18next';

interface KvkkModalProps {
  open: boolean;
  onClose: () => void;
}

const KvkkModal: React.FC<KvkkModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const rightsList = t('kvkkModal.rights.list', { returnObjects: true }) as string[];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        },
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #eee',
        pb: 2,
        m: 0,
      }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {t('kvkkModal.title')}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3, maxHeight: '70vh', '&::-webkit-scrollbar': { width: '8px' }, '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px'}, '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '4px'}, '&::-webkit-scrollbar-thumb:hover': { background: '#555'} }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
          {t('kvkkModal.greeting')}
        </Typography>
        <Typography paragraph variant="body2" sx={{ mb: 2 }}>
          {t('kvkkModal.intro')}
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1rem', color: 'primary.dark', mt: 2 }}>
          {t('kvkkModal.processing.title')}
        </Typography>
        <Typography paragraph variant="body2" sx={{ mb: 2 }}>
          {t('kvkkModal.processing.content')}
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1rem', color: 'primary.dark', mt: 2 }}>
          {t('kvkkModal.transfer.title')}
        </Typography>
        <Typography paragraph variant="body2" sx={{ mb: 2 }}>
          {t('kvkkModal.transfer.content')}
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1rem', color: 'primary.dark', mt: 2 }}>
          {t('kvkkModal.method.title')}
        </Typography>
        <Typography paragraph variant="body2" sx={{ mb: 2 }}>
          {t('kvkkModal.method.content')}
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1rem', color: 'primary.dark', mt: 2 }}>
          {t('kvkkModal.rights.title')}
        </Typography>
        <Typography component="div" variant="body2" sx={{ mb: 2 }}>
          {t('kvkkModal.rights.intro')}
          <ul>
            {Array.isArray(rightsList) && rightsList.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1rem', color: 'primary.dark', mt: 2 }}>
          {t('kvkkModal.controller.title')}
        </Typography>
        <Typography paragraph variant="body2">
          <Trans i18nKey="kvkkModal.controller.content" />
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary"
          sx={{ borderRadius: 2, px: 3 }}
        >
          {t('kvkkModal.closeButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KvkkModal; 